import querystring from 'querystring'
import { isUndefined, isEmpty, get } from 'lodash'
import { createAction } from 'redux-actions'
import pulse from '@blocket/tracking-pulse'

import { actionCreators as api } from 'reduxBits/api'
import { MAX_ADWATCHES_ERROR, MAX_SAVED_ADS_ERROR } from './errorTypes'

const prefix = 'Account:'
export const REQUEST_USER_INFORMATION = `${prefix} Request user information.`
export const RECEIVE_USER_INFORMATION = `${prefix} Receive user information.`
export const REQUEST_AD_WATCHES = `${prefix} Request ad watches.`
export const RECEIVE_AD_WATCHES = `${prefix} Receive ad watches.`
export const REQUEST_SAVED_ADS = `${prefix} Request saved ads.`
export const RECEIVE_SAVED_ADS = `${prefix} Receive saved ads.`
export const SET_SAVED_ADS_SHOW_ONLY_ACTIVE = `${prefix} Set wether we should show only active ads in saved ads`
export const REQUEST_DELETE_AD_WATCH = `${prefix} Request delete a adwatch.`
export const RECEIVE_DELETE_AD_WATCH = `${prefix} Receive delete a adwatch.`
export const REQUEST_CREATE_AD_WATCH = `${prefix} Request create a adwatch.`
export const RESET_SAVE_AD_WATCH_ERROR = `${prefix} Reset save adwatch error.`
export const RECEIVE_CREATE_AD_WATCH = `${prefix} Receive create a adwatch.`
export const REQUEST_DELETE_SAVED_AD = `${prefix} Request delete a saved ad.`
export const RECEIVE_DELETE_SAVED_AD = `${prefix} Receive delete a saved ad.`
export const REQUEST_CREATE_SAVED_AD = `${prefix} Request create a saved ad.`
export const RESET_SAVE_AD_ERROR = `${prefix} Reset save ad error.`
export const RECEIVE_CREATE_SAVED_AD = `${prefix} Receive create a saved ad.`
export const REQUEST_AD_WATCH_ADS = `${prefix} Request ads in ad watch`
export const RECEIVE_AD_WATCH_ADS = `${prefix} Receive ads in ad watch`
export const REQUEST_MARK_ADWATCHES_READ = `${prefix} Request marking an adwatch as read`
export const SET_ADWATCH_SHOW_ONLY_ACTIVE_ADS = `${prefix} Set wether we should show only active ads in adwatches`
export const REQUEST_UPDATE_ACCOUNT = `${prefix} Request update account`
export const RECEIVE_UPDATE_ACCOUNT = `${prefix} Receive update account`
export const RECEIVE_UPDATE_ACCOUNT_ERROR = `${prefix} Receive update account with error`
export const OVERVIEW_SAVED_AD_CLICKED = `${prefix} Overview saved ad clicked.`
export const SAVED_AD_CLICKED = `${prefix} Saved ad clicked.`
export const EDITING_SAVED_ADS = `${prefix} Editing saved ads`
export const ALL_ADWATCHES_CLICKED = `${prefix} All adwatches clicked.`
export const ADWATCH_CLICKED = `${prefix} Adwatch clicked.`
export const ADWATCH_AD_CLICKED = `${prefix} Adwatch ad clicked.`
export const MARK_ALL_ADWATCHES_READ = `${prefix} Mark all adwatches as read and reset counter.`
export const REQUEST_RESET_GLOABL_ADWATCH_COUNTER = `${prefix} Request reset global adwatch counter`

const requestUserInformation = createAction(REQUEST_USER_INFORMATION)
const receiveUserInformation = createAction(RECEIVE_USER_INFORMATION)
const requestAdWatches = createAction(REQUEST_AD_WATCHES)
const receiveAdWatches = createAction(RECEIVE_AD_WATCHES)
const requestSavedAds = createAction(REQUEST_SAVED_ADS)
const receiveSavedAds = createAction(RECEIVE_SAVED_ADS)
const showSavedAdsOnlyActive = createAction(SET_SAVED_ADS_SHOW_ONLY_ACTIVE)
const requestDeleteAdWatch = createAction(REQUEST_DELETE_AD_WATCH)
const receiveDeleteAdWatch = createAction(RECEIVE_DELETE_AD_WATCH)
const requestCreateAdWatch = createAction(REQUEST_CREATE_AD_WATCH)
const resetSaveAdWatchErrorAction = createAction(RESET_SAVE_AD_WATCH_ERROR)
const receiveCreateAdWatch = createAction(RECEIVE_CREATE_AD_WATCH)
const requestDeleteSavedAd = createAction(REQUEST_DELETE_SAVED_AD)
const receiveDeleteSavedAd = createAction(RECEIVE_DELETE_SAVED_AD)
const requestCreateSavedAd = createAction(REQUEST_CREATE_SAVED_AD)
const resetSavedAdErrorAction = createAction(RESET_SAVE_AD_ERROR)
const receiveCreateSavedAd = createAction(RECEIVE_CREATE_SAVED_AD)
const requestAdWatchAds = createAction(REQUEST_AD_WATCH_ADS)
const receiveAdWatchAds = createAction(RECEIVE_AD_WATCH_ADS)
const showAdwatchOnlyActiveAds = createAction(SET_ADWATCH_SHOW_ONLY_ACTIVE_ADS)
const requestUpdateAccount = createAction(REQUEST_UPDATE_ACCOUNT)
const receiveUpdateAccount = createAction(RECEIVE_UPDATE_ACCOUNT)
const receiveUpdateAccountError = createAction(RECEIVE_UPDATE_ACCOUNT_ERROR)
const overviewSavedAdClicked = createAction(OVERVIEW_SAVED_AD_CLICKED)
const savedAdClicked = createAction(SAVED_AD_CLICKED)
const editingSavedAds = createAction(EDITING_SAVED_ADS)
const allAdWatchesClicked = createAction(ALL_ADWATCHES_CLICKED)
const adWatchClicked = createAction(ADWATCH_CLICKED)
const adWatchAdClicked = createAction(ADWATCH_AD_CLICKED)
const requestResetGlobalAdwatchCounter = createAction(REQUEST_RESET_GLOABL_ADWATCH_COUNTER)
const requestMarkAdwatchesRead = createAction(REQUEST_MARK_ADWATCHES_READ)
const markAllWatchesReadAndResetCounter = createAction(MARK_ALL_ADWATCHES_READ)

const fetchUserInformation = (forceRefresh = false) => async (dispatch) => {
  dispatch(requestUserInformation())
  const response = await dispatch(api.fetch(`auth/v1/me?force_refresh=${forceRefresh}`))

  if (response.ok) {
    return dispatch(receiveUserInformation(response.textOrJson.data))
  }

  return dispatch(receiveUserInformation(response))
}

const fetchAdWatches = () => async (dispatch) => {
  dispatch(requestAdWatches())

  const response = await dispatch(
    api.fetch('search_bff/v2/saved_searches', {
      method: 'GET',
    }),
  )

  if (response.ok) {
    const { timestamp, data } = response.textOrJson
    return dispatch(
      receiveAdWatches({
        items: data,
        timestamp,
      }),
    )
  }

  return dispatch(receiveAdWatches(response))
}

const resetGlobalAdwatchCounter = () => async (dispatch) => {
  dispatch(requestResetGlobalAdwatchCounter()) // For traceability

  dispatch(
    api.fetch(`search_bff/v2/saved_searches_new_reset`, {
      method: 'POST',
    }),
  )
}

const markAdwatchesViewed = (adwatchId, timestamp) => (dispatch) => {
  const form = new FormData()
  form.append('timestamp', timestamp)

  return dispatch(
    api.fetch(`search_bff/v2/saved_searches_viewed/${adwatchId}`, {
      method: 'POST',
      body: form,
    }),
  )
}

const fetchAdWatchAds = (fetchAdwatchId, fetchPage, fetchSort) => async (dispatch, getState) => {
  const state = getState()
  const {
    numAdsPerPage,
    adwatchId: stateAdwatchId,
    page: statePage,
    sort: stateSort,
  } = state.account.adWatchAds
  const idParam = fetchAdwatchId === 'all' ? '' : `/${fetchAdwatchId}`
  const query = querystring.stringify({
    lim: numAdsPerPage,
    ...(fetchPage && { page: fetchPage > 1 ? fetchPage - 1 : 0 }),
    ...(fetchSort && { sort: fetchSort }),
  })

  // If all these match, we know that the data in the state can be reused and
  // the user probably clicked "back" from the VI of a ad watch ad
  if (fetchAdwatchId === stateAdwatchId && fetchPage === statePage && fetchSort === stateSort) {
    return null
  }

  dispatch(requestAdWatchAds())

  const response = await dispatch(
    api.fetch(`search_bff/v2/saved_searches_content${idParam}?${query}`),
  )

  if (response.ok) {
    if (fetchAdwatchId !== 'all') {
      dispatch(markAdwatchesViewed(fetchAdwatchId, response.textOrJson.timestamp))
    }

    return dispatch(
      receiveAdWatchAds({
        totalCount: response.textOrJson.totalCount,
        totalPageCount: response.textOrJson.totalPageCount,
        nextScrollId: response.textOrJson.nextScrollId,
        timestamp: response.textOrJson.timestamp,
        lastRead: response.textOrJson.lastRead,
        ads: response.textOrJson.data,
        adwatchId: fetchAdwatchId,
        page: fetchPage,
        sort: fetchSort,
      }),
    )
  }

  return dispatch(receiveAdWatchAds(response))
}

const markAllAdwatchesAsRead = () => async (dispatch, getState) => {
  const { timestamp } = getState().account.adWatches
  const form = new FormData()
  form.append('timestamp', timestamp)
  dispatch(markAllWatchesReadAndResetCounter()) // For traceability

  dispatch(
    api.fetch(`saved/v2/searches_read`, {
      method: 'POST',
      body: form,
    }),
  )
  await dispatch(fetchAdWatches())
  await dispatch(fetchAdWatchAds('all', 1))
}

const deleteAdWatch = (id) => async (dispatch) => {
  dispatch(requestDeleteAdWatch(id))

  const response = await dispatch(
    api.fetch(`search_bff/v2/saved_searches/${id}`, {
      method: 'DELETE',
    }),
  )

  if (response.ok) {
    return dispatch(receiveDeleteAdWatch(id))
  }

  return dispatch(receiveDeleteAdWatch(response))
}

const createAdWatch = (query) => async (dispatch, getState) => {
  dispatch(requestCreateAdWatch())

  const form = new FormData()
  form.append('query', query)

  const response = await dispatch(
    api.fetch(`search_bff/v2/saved_searches`, {
      method: 'POST',
      body: form,
    }),
  )

  if (!response.ok) {
    // We need to know if this error is expeceted due to the the number of adwatches being reached.
    // If a different type of error happes, we can't expect the `textOrJson` property to be present so let's be defensive about it
    if (
      response.textOrJson &&
      response.textOrJson.error &&
      response.textOrJson.error.toLowerCase() === 'too many adwatches'
    ) {
      response.errorType = MAX_ADWATCHES_ERROR
    }

    response.error = true
    return dispatch(receiveCreateAdWatch(response))
  }

  return dispatch(
    receiveCreateAdWatch({
      id: response.textOrJson.id,
      query: getState().classified.searchResults.querySignature,
    }),
  )
}

const resetAdWatchError = () => async (dispatch) => {
  dispatch(resetSaveAdWatchErrorAction())
}

const markAdwatchesRead = () => (dispatch, getState) => {
  const { timestamp } = getState().account.adWatches
  const form = new FormData()
  form.append('timestamp', timestamp)

  dispatch(requestMarkAdwatchesRead())

  return dispatch(
    api.fetch('search_bff/v2/saved_searches_read', {
      method: 'POST',
      body: form,
    }),
  )
}

const fetchSavedAds = () => async (dispatch) => {
  dispatch(requestSavedAds())
  const response = await dispatch(
    api.fetch('search_bff/v1/saved_content?lim=99', {
      method: 'GET',
    }),
  )
  if (response.ok) {
    return dispatch(receiveSavedAds(response.textOrJson))
  }
  return dispatch(receiveSavedAds(response))
}

const createSavedAd = (ad) => async (dispatch) => {
  dispatch(requestCreateSavedAd())

  const response = await dispatch(
    api.fetch(`search_bff/v1/saved_content/${ad.adId}`, {
      method: 'PUT',
    }),
  )

  if (response.ok) {
    pulse.event({
      type: 'Save',
      object: {
        type: 'ClassifiedAd',
        id: ad.adId,
      },
    })
    return dispatch(receiveCreateSavedAd(ad))
  }

  const errorMsg = get(response, ['textOrJson', 'error'], null)
  if (errorMsg === MAX_SAVED_ADS_ERROR) {
    response.errorType = MAX_SAVED_ADS_ERROR
  }

  return dispatch(receiveCreateSavedAd(response))
}

const resetSavedAdError = () => async (dispatch) => {
  dispatch(resetSavedAdErrorAction())
}

const deleteSavedAd = (ad) => async (dispatch) => {
  dispatch(requestDeleteSavedAd(ad))
  const response = await dispatch(
    api.fetch(`search_bff/v1/saved_content/${ad.adId}`, { method: 'DELETE' }),
  )

  if (response.ok) {
    pulse.event({
      type: 'Unsave',
      object: {
        type: 'ClassifiedAd',
        adType: ad.adType,
        id: ad.adId,
      },
    })
    return dispatch(receiveDeleteSavedAd(ad))
  }

  return dispatch(receiveDeleteSavedAd(response))
}

/**
 * Updates the blocket settings of the account
 *
 * @param {object} updatedProperties - the properties to update with its new values
 */
const updateAccountSettings = (updatedProperties) => async (dispatch, getState) => {
  // Setup model to send to API
  const settingsModel = {
    ...{
      region: getState().account.user.propertyMap.region,
      munic: getState().account.user.propertyMap.munic,
      subarea: getState().account.user.propertyMap.subarea,
      accept_newsletter: getState().account.user.propertyMap.acceptNewsletter,
      mc_send_email_copy: getState().account.user.propertyMap.mcSendEmailCopy,
      account_id: getState().account.user.propertyMap.accountId,
    },
    ...(isUndefined(updatedProperties) ? {} : updatedProperties),
  }

  // Set region, munic and region to str "0" when they are empty to avoid trans error
  settingsModel.region = isEmpty(settingsModel.region) ? '0' : settingsModel.region
  settingsModel.munic = isEmpty(settingsModel.munic) ? '0' : settingsModel.munic
  settingsModel.subarea = isEmpty(settingsModel.subarea) ? '0' : settingsModel.subarea

  // Send post to blocket API
  dispatch(requestUpdateAccount())

  const response = await dispatch(
    api.fetch('auth/v1/me/property_map', {
      method: 'PUT',
      body: JSON.stringify(settingsModel),
      headers: { 'Content-Type': 'application/json' },
    }),
  )

  if (!response.ok) {
    return dispatch(receiveUpdateAccountError(response))
  }

  return dispatch(receiveUpdateAccount(response.textOrJson.data))
}

export const actionCreators = {
  fetchUserInformation,
  fetchSavedAds,
  fetchAdWatches,
  fetchAdWatchAds,
  createAdWatch,
  resetAdWatchError,
  deleteAdWatch,
  createSavedAd,
  resetSavedAdError,
  deleteSavedAd,
  showSavedAdsOnlyActive,
  receiveAdWatches,
  receiveSavedAds,
  receiveUserInformation,
  showAdwatchOnlyActiveAds,
  updateAccountSettings,
  overviewSavedAdClicked,
  savedAdClicked,
  editingSavedAds,
  allAdWatchesClicked,
  adWatchClicked,
  adWatchAdClicked,
  resetGlobalAdwatchCounter,
  markAdwatchesRead,
  markAllAdwatchesAsRead,
}
